// adaptadorAgua.js
// Sustemia · Módulo Balance Hídrico · capa de herencia
//
// Reescrito contra el ESQUEMA REAL del módulo Agua (server/models/water.js),
// reemplazando la taxonomía "FEM" genérica que asumía la v1 de este archivo
// (client/Balance de agua/adaptador-agua-v1.js) sin conocer el esquema real.
//
// Todo el conocimiento del esquema de Agua vive acá, en ENTRADAS_WATER_FIELDS
// y en las funciones `leer*`. El motor (waterBalanceCalc.js), las fichas de
// campo (catalogs/casosDeCampo.js) y la UI no conocen un solo nombre de
// campo de Water ni de Production.
//
// Los objetos que devuelve este archivo usan nombres de campo en inglés
// (alineados con estado.js y con la convención de Water/Production/Social).
// Las funciones y los valores de catálogo (origen: "agua", estados como
// "sin_resolver") siguen en español — no son "la base", son vocabulario
// interno del módulo.
//
// REGLA DURA, sin excepción: el Balance LEE Agua. NUNCA escribe en Agua
// (ver Guía de campo §1.3). Las correcciones detectadas en campo salen como
// propuestas (`generarPropuestasDeCorreccion`) que aprueba el responsable de
// Agua — nunca se aplican solas.
//
// CUATRO HUECOS REALES respecto del contrato que imaginaba la guía (ver
// Sustemia_BalanceHidrico_GuiaCampo_ES_v3, §5bis, y el plan de
// implementación aprobado):
//   1. Water no registra método de medición, frecuencia ni vencimiento de
//      calibración por fuente — solo value/unit/records[]/files[]. Eso se
//      declara UNA VEZ por fuente como `sourceAnnotations` propia del
//      Balance (nunca escrita en Agua), enganchada por firma igual que
//      `herenciaDesactualizada`.
//   2. El riesgo hídrico (WRI Aqueduct / WWF Water Risk Filter) no existe en
//      ningún módulo hoy. Se pide como campo propio del Balance (context),
//      no como heredado.
//   3. La producción del período vive en el módulo Production, no en Agua.
//      Se lee de ahí (`leerProduccionDelPeriodo`), no de Water.
//   4. Días operativos y dotación tampoco viven en Agua, pero SÍ existen en
//      el módulo Social (server/models/social.js): `days_worked` y la
//      batería de campos de trabajadores por categoría/género. Se leen de
//      ahí (`leerDiasOperativosDelPeriodo` / `leerDotacionDelPeriodo`), igual
//      que la producción se lee de Production — un tercer módulo que el
//      Balance solo lee, nunca escribe.

export const ESQUEMA_ORIGEN = {
  version: "water_real_v1",
  confirmadoPorEquipoAgua: true,
  nota: "Mapeo contra server/models/water.js real, no contra una taxonomía asumida.",
};

// Campos medibles de Water que representan un volumen de ENTRADA a la
// planta (se excluyen los campos water_cost_* y *_cost: son costo, no
// volumen, y quedan para el costeo MFCA de una etapa futura).
export const ENTRADAS_WATER_FIELDS = [
  "water_domestic_municipal_network",
  "municipal_network_water_production",
  "combined_municipal_network_water",
  "domestic_groundwater",
  "groundwater_production",
  "groundwater_combined",
  "rainwater",
  "surface_water",
  "reused_water",
  "recycled_water",
  "steam_condensed_water",
  "distilled_water",
];

const FACTORES_UNIDAD = { m3: 1, "m³": 1, dam3: 1000, hm3: 1e6, l: 0.001, litros: 0.001, liters: 0.001 };

/**
 * Normaliza un valor a m³. Unidad ausente se asume m³ (es la convención real
 * de este módulo hoy). Unidad presente pero no reconocida devuelve error
 * explícito, nunca un cero silencioso.
 */
export function normalizarAM3(valor, unidad) {
  if (valor == null) return null;
  if (!unidad) return valor;
  const clave = String(unidad).trim().toLowerCase();
  const factor = FACTORES_UNIDAD[clave];
  if (factor == null) return { valor: null, error: "unidad_desconocida", unidadOrigen: unidad };
  return valor * factor;
}

function firmaDeOrigen(campo) {
  return { volume: campo.value ?? null, updatedAt: campo.updatedAt ? String(campo.updatedAt) : null };
}

/**
 * Lee el período de Agua (y de Production y Social) y devuelve el fragmento
 * de `estado` que el motor del Balance consume. Puro: no muta el origen.
 *
 * @param {object|null} waterDoc         - doc de Water para el mismo site+year+period
 * @param {object|null} productionDoc    - doc de Production para el mismo site+year+period
 * @param {Array} sourceAnnotations      - anotaciones propias del Balance (método/calibración
 *                                          declarados por el usuario, ver balanceHidrico.js)
 * @param {object|null} socialDoc        - doc de Social para el mismo site+year+period
 */
export function leerPeriodoAgua(waterDoc, productionDoc, sourceAnnotations = [], socialDoc = null) {
  const inputs = ENTRADAS_WATER_FIELDS
    .map((sourceId) => {
      const campo = waterDoc?.[sourceId];
      if (!campo || campo.value == null) return null;

      const volumeM3 = normalizarAM3(campo.value, campo.unit);
      const annotation = sourceAnnotations.find((a) => a.sourceId === sourceId);
      // Proxy débil de método mientras no haya anotación declarada: un
      // registro cargado por el lector de facturas se trata como "factura",
      // uno manual sin más info queda como "estim_sin" hasta que alguien lo
      // declare (ver hallazgo 1 arriba).
      const methodProxy = (campo.records || []).some((r) => r.source === "invoice_reader") ? "factura" : "estim_sin";

      return {
        sourceId,
        volumeM3,
        evidenceIds: (campo.files || []).map((f) => f.uniqueName),
        hasEvidence: (campo.files || []).length > 0,
        declaredMethod: annotation?.declaredMethod || methodProxy,
        calibrationDue: annotation?.calibrationDue || null,
        methodConfirmedByUser: !!annotation,
        origin: "agua",
        editableInBalance: false, // se corrige en Agua, no acá (regla dura §1.3)
        signature: firmaDeOrigen(campo),
      };
    })
    .filter(Boolean);

  return {
    inputs,
    context: {
      operatingDays: leerDiasOperativosDelPeriodo(socialDoc),
      staffing: leerDotacionDelPeriodo(socialDoc),
      // El riesgo hídrico NO existe en ningún módulo hoy: queda sin poblar a
      // propósito. Se completa a mano en el paso "Contexto" del Balance (ver
      // hallazgo 2).
      wriRisk: null,
      wwfRisk: null,
      production: leerProduccionDelPeriodo(productionDoc),
    },
    // Lo que Agua no tiene. Se declara vacío en vez de omitirse, para que la
    // UI pueda decir "esto es nuevo" en vez de simplemente no mostrarlo.
    outputs: [],
    centers: [],
    storage: [],
    recirculations: [],
    costs: null,
  };
}

/** La producción del período vive en Production, no en Agua (hallazgo 3). */
export function leerProduccionDelPeriodo(productionDoc) {
  const value = productionDoc?.production_volume?.value;
  if (value == null) return null;
  return { value, unit: productionDoc.production_volume.unit || null };
}

/** Días operativos: `days_worked` del período en Social (hallazgo 4). */
export function leerDiasOperativosDelPeriodo(socialDoc) {
  return socialDoc?.days_worked?.value ?? null;
}

// Dotación = suma de los campos de trabajadores por categoría y género que
// ya registra Social. Más rico que un número único: separa producción de
// administrativo, y permanentes de temporarios.
const CAMPOS_DOTACION_SOCIAL = [
  "female_production_workers",
  "male_production_workers",
  "other_gender_production_workers",
  "female_administrative_workers",
  "male_administrative_workers",
  "administrative_workers_other_genders",
  "female_temporary_workers",
  "male_temporary_workers",
  "temporary_workers_other_genders",
];

/** Dotación del período: suma de los campos de trabajadores en Social (hallazgo 4). */
export function leerDotacionDelPeriodo(socialDoc) {
  if (!socialDoc) return null;
  const total = CAMPOS_DOTACION_SOCIAL.reduce((a, campo) => a + (Number(socialDoc[campo]?.value) || 0), 0);
  return total > 0 ? total : null;
}

/** true si el dato heredado quedó desactualizado respecto de Agua. */
export function herenciaDesactualizada(flujoHeredado, campoActualEnWater) {
  const signature = flujoHeredado?.signature;
  if (!signature || !campoActualEnWater) return false;
  const current = firmaDeOrigen(campoActualEnWater);
  return signature.volume !== current.volume || signature.updatedAt !== current.updatedAt;
}

// ---------------------------------------------------------------------------
// DIVERGENCIA — el campo dice otra cosa que Agua. Ninguna resolución "pisa"
// el valor de Agua (Guía de campo §1.4).
// ---------------------------------------------------------------------------

export const OPCIONES_RESOLUCION_DIVERGENCIA = [
  { id: "agua_tiene_razon", efecto: "se_mantiene_heredado", requiere: null },
  { id: "campo_tiene_razon", efecto: "propuesta_de_correccion_a_agua", requiere: "evidencia_nueva" },
  { id: "son_periodos_distintos", efecto: "revisar_empalme_de_periodo", requiere: null },
];

export function detectarDivergencias(flujosHeredados, lecturasDeCampo, toleranciaPct = 0) {
  const out = [];
  for (const lectura of lecturasDeCampo) {
    const h = flujosHeredados.find((f) => f.sourceId === lectura.sourceId);
    if (!h || h.volumeM3 == null || lectura.volumeM3 == null) continue;
    const deltaPct = h.volumeM3 === 0 ? null : ((lectura.volumeM3 - h.volumeM3) / h.volumeM3) * 100;
    if (deltaPct !== null && Math.abs(deltaPct) > toleranciaPct) {
      out.push({
        sourceId: lectura.sourceId,
        waterValue: h.volumeM3,
        fieldValue: lectura.volumeM3,
        deltaPct: +deltaPct.toFixed(2),
        status: "sin_resolver",
        opciones: OPCIONES_RESOLUCION_DIVERGENCIA.map((o) => o.id),
      });
    }
  }
  return out;
}

/**
 * Convierte lo hallado en campo en propuestas para el responsable de Agua.
 * Nunca se aplican solas (regla dura del §1.3).
 */
export function generarPropuestasDeCorreccion(divergenciasResueltas, hallazgosDeCampo) {
  const props = [];

  for (const d of divergenciasResueltas.filter((x) => x.status === "campo_tiene_razon")) {
    props.push({
      type: "corregir_volumen",
      sourceId: d.sourceId,
      proposedValue: d.fieldValue,
      currentValue: d.waterValue,
      evidenceId: d.newEvidenceId ?? null,
      requiereAprobacionDe: "responsable_modulo_agua",
    });
  }

  for (const h of hallazgosDeCampo ?? []) {
    if (h.tipo === "calibracion_vencida_constatada") {
      props.push({
        type: "actualizar_calibracion",
        sourceId: h.sourceId,
        detail: h.detalle,
        efectoEnAgua: "baja_grado_de_validez_de_la_fuente",
        requiereAprobacionDe: "responsable_modulo_agua",
      });
    }
    if (h.tipo === "fuente_no_declarada") {
      props.push({
        type: "alta_de_fuente",
        detail: h.detalle,
        efectoEnAgua: "cambia_el_consumo_total_del_periodo",
        requiereAprobacionDe: "responsable_modulo_agua",
      });
    }
  }
  return props;
}

// ---------------------------------------------------------------------------
// COBERTURA POR CASO DE CAMPO — calculada del dato, nunca declarada
// (Guía de campo §1.2).
// ---------------------------------------------------------------------------

// Qué campos de cada caso vienen heredados de Agua/Production (`hereda`) y
// cuáles hay que conseguir en planta (`pide`). Mismo criterio que
// catalogs/casosDeCampo.js (yaEstaEnAgua / hayQueConseguir), en forma de
// lista para poder calcular el porcentaje real. Los nombres en `hereda`
// tienen que coincidir con las claves reales de `leerPeriodoAgua()`.
export const HERENCIA_POR_CASO = {
  A1_POZO_MACROMEDIDOR: { hereda: ["sourceId", "volumeM3", "hasEvidence"], pide: ["volumenAutorizadoPermiso", "centroDeDestino"] },
  A2_TORRE_POR_DIFERENCIA: { hereda: [], pide: ["reposicionMedida", "purgaMedida"] },
  B1_MEDIDOR_COMPARTIDO: { hereda: ["volumeM3", "hasEvidence"], pide: ["registroDeCargas", "relacionDeBanoDeFicha", "litrosPorPersonaDia"] },
  B2_VARIACION_INVENTARIO: { hereda: [], pide: ["geometriaDelTanque", "nivelInicial", "nivelFinal"] },
  C1_LAVANDERIA_RELACION_BANO: { hereda: ["production"], pide: ["prendasProcesadas", "pesoMedioPorPrenda", "relacionDeBanoAcumulada"] },
  C2_CIERRE_FACTURA_Y_TANQUE: { hereda: ["volumeM3", "hasEvidence"], pide: ["efluenteMedido", "evaporacionEstimada", "riegoEstimado", "deltaInventario"] },
};

export function coberturaDeCaso(casoId, estadoHeredado) {
  const h = HERENCIA_POR_CASO[casoId];
  if (!h) return null;
  const total = h.hereda.length + h.pide.length;
  const resueltos = h.hereda.filter((campo) => tieneValor(estadoHeredado, campo)).length;
  return {
    casoId,
    camposHeredadosConDato: resueltos,
    camposHeredadosEsperados: h.hereda.length,
    camposNuevos: h.pide.length,
    pctResuelto: total ? +((resueltos / total) * 100).toFixed(1) : 0,
    sinHerencia: h.hereda.length === 0,
  };
}

function tieneValor(estado, campoLogico) {
  if (!estado) return false;
  if (campoLogico === "production") return estado.context?.production?.value != null;
  if (campoLogico === "sourceId" || campoLogico === "volumeM3" || campoLogico === "hasEvidence") {
    return (estado.inputs || []).some((f) => f[campoLogico] != null && f[campoLogico] !== false);
  }
  return estado[campoLogico] != null;
}

export default {
  ESQUEMA_ORIGEN,
  ENTRADAS_WATER_FIELDS,
  normalizarAM3,
  leerPeriodoAgua,
  leerProduccionDelPeriodo,
  leerDiasOperativosDelPeriodo,
  leerDotacionDelPeriodo,
  herenciaDesactualizada,
  OPCIONES_RESOLUCION_DIVERGENCIA,
  detectarDivergencias,
  generarPropuestasDeCorreccion,
  HERENCIA_POR_CASO,
  coberturaDeCaso,
};
